/**
 * Profit Gap Details Modal
 * Displays detailed breakdown of profit gap (EBITDA) calculation
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, X, Download } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import { useBusinessContext } from '../../contexts/BusinessContext';

const ProfitGapDetailsModal = ({ show, onClose, profitGap }) => {
  const { currentBusinessId } = useBusinessContext();
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    if (!currentBusinessId) {
      alert('No business selected');
      return;
    }

    setDownloadingPDF(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/profit-gap-details/${currentBusinessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': currentBusinessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'Profit_Gap_Details.pdf';

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (!show || !profitGap) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header - Responsive */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 p-4 sm:p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <TrendingUp size={24} className="text-white flex-shrink-0 sm:w-7 sm:h-7" />
            <div className="min-w-0">
              <h3 className="text-lg sm:text-2xl font-bold text-white truncate">Profit Gap Details</h3>
              <p className="text-purple-100 text-xs sm:text-sm hidden sm:block">EBITDA Performance Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="text-white/90 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]"
              title="Download PDF"
            >
              <Download className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">
                {downloadingPDF ? 'Generating...' : 'PDF'}
              </span>
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Responsive padding */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Summary */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-bold text-lg text-gray-900 mb-3">Summary</h4>
            <p className="text-gray-700 text-sm mb-4">
              Your profit gap measures the additional business value you could unlock by improving your EBITDA margin to industry benchmark levels.
            </p>

            {/* Two scenarios side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Median Industry Margin */}
              <div className="bg-white rounded-lg p-4 border border-blue-300">
                <p className="text-sm font-semibold text-blue-900 mb-2">At Median Industry Margin</p>
                <p className="text-xs text-blue-700 mb-3">({profitGap.industry_median_ebitda_margin.toFixed(1)}% EBITDA margin)</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-700">Valuation Impact:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(profitGap.valuation_impact_median)}
                  </span>
                </div>
              </div>

              {/* Best in Class Margin */}
              <div className="bg-white rounded-lg p-4 border border-green-300">
                <p className="text-sm font-semibold text-green-900 mb-2">At Best in Class Margin</p>
                <p className="text-xs text-green-700 mb-3">({profitGap.industry_high_ebitda_margin.toFixed(1)}% EBITDA margin)</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-700">Valuation Impact:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(profitGap.valuation_impact_best)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Industry Benchmark */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Industry Benchmark</h4>
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-1">Industry:</p>
                <p className="font-bold text-lg text-indigo-900">{profitGap.industry_name || 'Not specified'}</p>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-indigo-200">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Your EBITDA Margin</p>
                  <p className="text-2xl font-bold text-amber-600">{profitGap.current_ebitda_margin.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Median Industry Margin</p>
                  <p className="text-2xl font-bold text-blue-600">{profitGap.industry_median_ebitda_margin.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Best in Class Margin</p>
                  <p className="text-2xl font-bold text-emerald-600">{profitGap.industry_high_ebitda_margin.toFixed(1)}%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-indigo-200">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Gap to Median</p>
                  <p className="text-lg font-bold text-blue-600">
                    {(profitGap.industry_median_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage points
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Gap to Best in Class</p>
                  <p className="text-lg font-bold text-green-600">
                    {(profitGap.industry_high_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage points
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Current Performance */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Current Performance</h4>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Revenue:</span>
                <span className="font-semibold">{formatCurrency(profitGap.current_revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current EBITDA:</span>
                <span className="font-semibold">{formatCurrency(profitGap.current_ebitda)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current EBITDA Margin:</span>
                <span className="font-semibold text-amber-600">{profitGap.current_ebitda_margin.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-300">
                <span className="text-sm text-gray-600">Target EBITDA (at median margin):</span>
                <span className="font-semibold text-blue-600">{formatCurrency(profitGap.target_ebitda_median)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Target EBITDA (at best in class):</span>
                <span className="font-semibold text-green-600">{formatCurrency(profitGap.target_ebitda_best)}</span>
              </div>
            </div>
          </div>

          {/* Step-by-Step Calculation */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Step-by-Step Calculations (Value Gap Framework)</h4>

            {/* Common Step: Position Calculation */}
            <div className="mb-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
              <p className="text-sm font-semibold text-purple-900 mb-2">Assessment Score Position</p>
              <p className="text-sm text-purple-800">
                Position = (Assessment Score - 17) / 83 × 100
              </p>
              <p className="text-sm text-purple-800 mt-1">
                = ({profitGap.assessment_score?.toFixed(1)}% - 17) / 83 × 100
              </p>
              <p className="text-md font-bold text-purple-900 mt-1">
                = {(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-purple-700 mt-2 italic">This position is used to calculate valuation within the range for all scenarios.</p>
            </div>

            {/* Median Scenario */}
            <div className="mb-6">
              <h5 className="font-semibold text-md text-blue-900 mb-3">Scenario 1: At Median Industry Margin ({profitGap.industry_median_ebitda_margin.toFixed(1)}%)</h5>
              <div className="space-y-3">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 1: Calculate New EBITDA at Median Margin</p>
                  <p className="text-sm text-blue-800">
                    New EBITDA = Revenue × Median Margin
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    = {formatCurrency(profitGap.current_revenue)} × {profitGap.industry_median_ebitda_margin.toFixed(1)}%
                  </p>
                  <p className="text-lg font-bold text-blue-900 mt-2">
                    = {formatCurrency(profitGap.target_ebitda_median)}
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 2: Calculate Scaling Factor</p>
                  <p className="text-sm text-blue-800">
                    Scaling Factor = New EBITDA / Current EBITDA
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    = {formatCurrency(profitGap.target_ebitda_median)} / {formatCurrency(profitGap.current_ebitda)}
                  </p>
                  <p className="text-lg font-bold text-blue-900 mt-2">
                    = {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 3: Scale Valuation Range</p>
                  <p className="text-sm text-blue-800">
                    New Low = Current Low × Scaling Factor
                  </p>
                  <p className="text-sm text-blue-800">
                    = {formatCurrency(profitGap.valuation_low)} × {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                  </p>
                  <p className="text-sm font-bold text-blue-900 mt-1">
                    = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-blue-800 mt-3">
                    New High = Current High × Scaling Factor
                  </p>
                  <p className="text-sm text-blue-800">
                    = {formatCurrency(profitGap.valuation_high)} × {(profitGap.target_ebitda_median / profitGap.current_ebitda).toFixed(3)}
                  </p>
                  <p className="text-sm font-bold text-blue-900 mt-1">
                    = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 4: Calculate Valuation at Position</p>
                  <p className="text-sm text-blue-800">
                    Valuation = New Low + (New Range × Position / 100)
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    New Range = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_median / profitGap.current_ebitda))} - {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-blue-800">
                    = {formatCurrency((profitGap.valuation_high - profitGap.valuation_low) * (profitGap.target_ebitda_median / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    Valuation = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_median / profitGap.current_ebitda))} + ({formatCurrency((profitGap.valuation_high - profitGap.valuation_low) * (profitGap.target_ebitda_median / profitGap.current_ebitda))} × {(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-blue-900 mt-2">
                    = {formatCurrency(profitGap.median_valuation)}
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 5: Calculate Valuation Impact</p>
                  <p className="text-sm text-blue-800">
                    Impact = Median Valuation - Current Valuation
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    = {formatCurrency(profitGap.median_valuation)} - {formatCurrency(profitGap.current_valuation)}
                  </p>
                  <p className="text-2xl font-bold text-blue-900 mt-2">
                    = {formatCurrency(profitGap.valuation_impact_median)}
                  </p>
                </div>
              </div>
            </div>

            {/* Best in Class Scenario */}
            <div>
              <h5 className="font-semibold text-md text-green-900 mb-3">Scenario 2: At Best in Class Margin ({profitGap.industry_high_ebitda_margin.toFixed(1)}%)</h5>
              <div className="space-y-3">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 1: Calculate New EBITDA at Best in Class Margin</p>
                  <p className="text-sm text-green-800">
                    New EBITDA = Revenue × Best in Class Margin
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    = {formatCurrency(profitGap.current_revenue)} × {profitGap.industry_high_ebitda_margin.toFixed(1)}%
                  </p>
                  <p className="text-lg font-bold text-green-900 mt-2">
                    = {formatCurrency(profitGap.target_ebitda_best)}
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 2: Calculate Scaling Factor</p>
                  <p className="text-sm text-green-800">
                    Scaling Factor = New EBITDA / Current EBITDA
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    = {formatCurrency(profitGap.target_ebitda_best)} / {formatCurrency(profitGap.current_ebitda)}
                  </p>
                  <p className="text-lg font-bold text-green-900 mt-2">
                    = {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 3: Scale Valuation Range</p>
                  <p className="text-sm text-green-800">
                    New Low = Current Low × Scaling Factor
                  </p>
                  <p className="text-sm text-green-800">
                    = {formatCurrency(profitGap.valuation_low)} × {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                  </p>
                  <p className="text-sm font-bold text-green-900 mt-1">
                    = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-green-800 mt-3">
                    New High = Current High × Scaling Factor
                  </p>
                  <p className="text-sm text-green-800">
                    = {formatCurrency(profitGap.valuation_high)} × {(profitGap.target_ebitda_best / profitGap.current_ebitda).toFixed(3)}
                  </p>
                  <p className="text-sm font-bold text-green-900 mt-1">
                    = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 4: Calculate Valuation at Position</p>
                  <p className="text-sm text-green-800">
                    Valuation = New Low + (New Range × Position / 100)
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    New Range = {formatCurrency(profitGap.valuation_high * (profitGap.target_ebitda_best / profitGap.current_ebitda))} - {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-green-800">
                    = {formatCurrency((profitGap.valuation_high - profitGap.valuation_low) * (profitGap.target_ebitda_best / profitGap.current_ebitda))}
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    Valuation = {formatCurrency(profitGap.valuation_low * (profitGap.target_ebitda_best / profitGap.current_ebitda))} + ({formatCurrency((profitGap.valuation_high - profitGap.valuation_low) * (profitGap.target_ebitda_best / profitGap.current_ebitda))} × {(((profitGap.assessment_score - 17) / 83) * 100).toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-green-900 mt-2">
                    = {formatCurrency(profitGap.best_valuation)}
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 5: Calculate Valuation Impact</p>
                  <p className="text-sm text-green-800">
                    Impact = Best in Class Valuation - Current Valuation
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    = {formatCurrency(profitGap.best_valuation)} - {formatCurrency(profitGap.current_valuation)}
                  </p>
                  <p className="text-2xl font-bold text-green-900 mt-2">
                    = {formatCurrency(profitGap.valuation_impact_best)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Interpretation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-3">What This Means</h4>

            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-900">Median Industry Margin Scenario:</span> By improving your EBITDA margin from <span className="font-semibold">{profitGap.current_ebitda_margin.toFixed(1)}%</span> to
                  the <span className="font-semibold">{profitGap.industry_name || 'industry'}</span> median of <span className="font-semibold">{profitGap.industry_median_ebitda_margin.toFixed(1)}%</span>,
                  you could add <span className="font-semibold text-blue-600">{formatCurrency(profitGap.valuation_impact_median)}</span> to your business value.
                </p>
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-green-900">Best in Class Scenario:</span> By improving your EBITDA margin to
                  the <span className="font-semibold">{profitGap.industry_name || 'industry'}</span> best in class of <span className="font-semibold">{profitGap.industry_high_ebitda_margin.toFixed(1)}%</span>,
                  you could add <span className="font-semibold text-green-600">{formatCurrency(profitGap.valuation_impact_best)}</span> to your business value.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-700 mt-4">
              These calculations use the EBITDA multiple of <span className="font-semibold">{profitGap.ebitda_multiple}x</span> from your most recent valuation
              and apply a <span className="font-semibold">{(profitGap.private_company_discount * 100).toFixed(0)}%</span> private company discount.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Focus on increasing profitability through operational efficiency, cost management, and revenue optimization. Even reaching the median margin ({profitGap.industry_median_ebitda_margin.toFixed(1)}%)
              represents a {(profitGap.industry_median_ebitda_margin - profitGap.current_ebitda_margin).toFixed(1)} percentage point improvement
              and significant value creation opportunity.
            </p>
          </div>
        </div>

        {/* Footer - Responsive */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

ProfitGapDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  profitGap: PropTypes.shape({
    assessment_score: PropTypes.number,
    current_valuation: PropTypes.number,
    median_valuation: PropTypes.number,
    best_valuation: PropTypes.number,
    valuation_gap: PropTypes.number,
    industry_name: PropTypes.string,
    current_ebitda_margin: PropTypes.number,
    industry_median_ebitda_margin: PropTypes.number,
    industry_high_ebitda_margin: PropTypes.number,
    current_revenue: PropTypes.number,
    current_ebitda: PropTypes.number,
    target_ebitda: PropTypes.number,
    target_ebitda_median: PropTypes.number,
    target_ebitda_best: PropTypes.number,
    ebitda_gap: PropTypes.number,
    ebitda_gap_median: PropTypes.number,
    ebitda_gap_best: PropTypes.number,
    valuation_impact_median: PropTypes.number,
    valuation_impact_best: PropTypes.number,
    ebitda_multiple: PropTypes.number,
    private_company_discount: PropTypes.number
  })
};

export default ProfitGapDetailsModal;
